import { ChangeEvent, FC, useEffect } from 'react';

import {
  Col,
  Divider,
  Flex,
  Form,
  Input,
  Radio,
  RadioChangeEvent,
  Row,
  Select,
  Typography,
} from 'antd';
import { FormInstance } from 'antd/lib';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import IconInfo from '@/assets/icons/icon-info.svg?react';
import RTooltip from '@/components/RTooltip';
import useCurrency from '@/contexts/useCurrency';
import { StorageSystemForm } from '@/types/storageSystem';
import {
  formatInputNumber,
  normalizePositiveIntegerValuesInput,
  normalizePositiveNumericValuesInput,
  removeCommas,
} from '@/utils';

type Props = {
  disabled?: boolean;
  form: FormInstance<StorageSystemForm>;
  initialValues?: StorageSystemForm;
};

const { Option } = Select;

const FinancialMetrics: FC<Props> = ({
  disabled = false,
  form,
  initialValues,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { currencySymbol } = useCurrency();

  const initialYearCount = form.getFieldValue('investmentLag');

  const initialPreopCapexDistribution = form.getFieldValue(
    'preopCapexDistribution'
  );
  const yearCount = Form.useWatch('investmentLag', form) || initialYearCount;
  const preopCapexDistribution =
    Form.useWatch('preopCapexDistribution', form) ||
    initialPreopCapexDistribution;

  const loanFinancing = Form.useWatch('loanFinancing', form) || 0;
  const loanRepaymentPeriodBattery = Form.useWatch(
    'loanRepaymentPeriodBattery',
    form
  );
  const loanRepaymentPeriodBos = Form.useWatch('loanRepaymentPeriodBos', form);

  const handleLoanFinancingChange = (e: RadioChangeEvent) => {
    const value = e.target.value;
    form.setFieldValue('loanFinancing', value);

    if (value === 0) {
      form.setFieldsValue({
        loanRepaymentPeriodBattery: 1,
        loanRepaymentPeriodBos: 1,
        yearlyInterestExpenseBattery: [0],
        yearlyInterestExpenseBos: [0],
        loanFundedCapexRatioBattery: 0,
        loanFundedCapexRatioBos: 0,
      });
    }
  };

  const handleLoanRepaymentPeriodChangeBattery = () => {
    const currentPeriod = form.getFieldValue('loanRepaymentPeriodBattery');
    const prevArray = form.getFieldValue('yearlyInterestExpenseBattery') || [];
    let newArray = [];
    if (prevArray.length > currentPeriod) {
      newArray = prevArray.slice(0, currentPeriod);
    } else {
      newArray = [
        ...prevArray,
        ...Array(currentPeriod - prevArray.length).fill(0),
      ];
    }
    form.setFieldsValue({
      yearlyInterestExpenseBattery: newArray,
    });
  };
  const handleLoanRepaymentPeriodChangeBos = () => {
    const currentPeriod = form.getFieldValue('loanRepaymentPeriodBos');
    const prevArray = form.getFieldValue('yearlyInterestExpenseBos') || [];
    let newArray = [];
    if (prevArray.length > currentPeriod) {
      newArray = prevArray.slice(0, currentPeriod);
    } else {
      newArray = [
        ...prevArray,
        ...Array(currentPeriod - prevArray.length).fill(0),
      ];
    }
    form.setFieldsValue({
      yearlyInterestExpenseBos: newArray,
    });
  };

  useEffect(() => {
    if (form) {
      if (initialValues?.name) {
        const initialYearCount = initialValues.investmentLag;
        if (yearCount != initialYearCount) {
          const year = Number(yearCount) + 1;
          form.setFieldValue('preopCapexDistribution', Array(year).fill(''));
        }
        if (yearCount == initialYearCount) {
          form.setFieldValue(
            'preopCapexDistribution',
            initialValues.preopCapexDistribution
          );
        }
      } else {
        const year = Number(yearCount) + 1;
        if (form.isFieldTouched('investmentLag')) {
          form.setFieldValue('preopCapexDistribution', Array(year).fill(''));
        }
      }
    }
  }, [form, initialValues, yearCount]);

  // TODO: refactor this code use useWatch or formOnChange instead of useEffect
  useEffect(() => {
    if (form.isFieldTouched('investmentLag')) {
      const yearCount =
        parseInt(form.getFieldValue('investmentLag') || 0, 10) + 1;

      if (yearCount === 1) {
        form.setFieldsValue({
          preopCapexDistribution: Array(yearCount).fill(100),
        });
      }
    }
  }, [form, yearCount]);

  // Update yearlyInterestExpense arrays when loan repayment periods change
  useEffect(() => {
    if (
      loanRepaymentPeriodBattery !== undefined &&
      loanRepaymentPeriodBattery !== null
    ) {
      const prevArray =
        form.getFieldValue('yearlyInterestExpenseBattery') || [];
      let newArray = [];
      if (prevArray.length > loanRepaymentPeriodBattery) {
        newArray = prevArray.slice(0, loanRepaymentPeriodBattery);
      } else {
        newArray = [
          ...prevArray,
          ...Array(loanRepaymentPeriodBattery - prevArray.length).fill(0),
        ];
      }
      form.setFieldsValue({
        yearlyInterestExpenseBattery: newArray,
      });
    }
  }, [loanRepaymentPeriodBattery, form]);

  useEffect(() => {
    if (
      loanRepaymentPeriodBos !== undefined &&
      loanRepaymentPeriodBos !== null
    ) {
      const prevArray = form.getFieldValue('yearlyInterestExpenseBos') || [];
      let newArray = [];
      if (prevArray.length > loanRepaymentPeriodBos) {
        newArray = prevArray.slice(0, loanRepaymentPeriodBos);
      } else {
        newArray = [
          ...prevArray,
          ...Array(loanRepaymentPeriodBos - prevArray.length).fill(0),
        ];
      }
      form.setFieldsValue({
        yearlyInterestExpenseBos: newArray,
      });
    }
  }, [loanRepaymentPeriodBos, form]);

  useEffect(() => {
    if (form && preopCapexDistribution) {
      const validateTotal = () => {
        // Calculate the total value
        const total = preopCapexDistribution.reduce(
          (sum: number, item: string) => {
            const value = parseFloat(item) || 0;
            return sum + value;
          },
          0
        );

        if (total !== 100) {
          // Set form errors if the total is not exactly 100
          form.setFields(
            preopCapexDistribution.map((_: unknown, index: number) => ({
              name: [`preopCapexDistribution`, index],
              errors: [
                total > 100
                  ? t(
                      'storageSystem.form.financialMetrics.validation.totalPreopCapexDistribution'
                    )
                  : t(
                      'storageSystem.form.financialMetrics.validation.totalPreopCapexDistributionExact'
                    ),
              ],
            }))
          );
        } else {
          // Clear errors if total is exactly 100
          form.setFields(
            preopCapexDistribution.map((_: unknown, index: number) => ({
              name: [`preopCapexDistribution`, index],
              errors: [],
            }))
          );
        }
      };
      validateTotal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preopCapexDistribution]);

  const handleNumberChange =
    (field: keyof StorageSystemForm) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value.replace(/[^0-9.]/g, '');
      const formatted = formatInputNumber(rawValue);
      form?.setFieldValue(field, formatted);
    };

  const handleNumberBlur = (field: keyof StorageSystemForm) => () => {
    const value = form?.getFieldValue(field) || '';
    form?.setFieldValue(field, removeCommas(value));
  };

  const energyStorageComponentsUnitOptions = (
    <Form.Item<StorageSystemForm>
      style={{ margin: '0px' }}
      name="batteryCapexUnit"
      initialValue="dollars"
    >
      <Select disabled={disabled} style={{ width: 90 }}>
        <Option value="dollars">{currencySymbol}</Option>
        <Option value="dollars_per_mw">{currencySymbol}/MWh</Option>
      </Select>
    </Form.Item>
  );

  const pcsCostUnitOptions = (
    <Form.Item<StorageSystemForm>
      initialValue="dollars"
      style={{ margin: '0px' }}
      name="pcsCostUnit"
    >
      <Select disabled={disabled} style={{ width: 90 }}>
        <Option value="dollars">{currencySymbol}</Option>
        <Option value="dollars_per_mw">{currencySymbol}/MW</Option>
      </Select>
    </Form.Item>
  );

  const bosCostUnitOptions = (
    <Form.Item<StorageSystemForm>
      style={{ margin: '0px' }}
      name="bosCostUnit"
      initialValue="dollars"
    >
      <Select disabled={disabled} style={{ width: 90 }}>
        <Option value="dollars">{currencySymbol}</Option>
        <Option value="dollars_per_mw">{currencySymbol}/MW</Option>
      </Select>
    </Form.Item>
  );

  return (
    <Form.Item
      style={{
        border: '0.5px solid',
        borderRadius: '10px',
        borderColor: theme.colors.grayLightAccent,
      }}
      className="p-16"
      wrapperCol={{ span: 24 }}
    >
      <Row>
        <Col className="pr-8" span={12}>
          <Form.Item<StorageSystemForm>
            name="investmentLag"
            label={
              <Flex gap={8} align="center">
                <Typography.Text className="fs-14-regular text-black-color">
                  {t('storageSystem.form.financialMetrics.investmentLag.label')}
                </Typography.Text>
                <RTooltip
                  title={t(
                    'storageSystem.form.financialMetrics.investmentLag.tooltip.title'
                  )}
                  description={t(
                    'storageSystem.form.financialMetrics.investmentLag.tooltip.description'
                  )}
                >
                  <IconInfo />
                </RTooltip>
              </Flex>
            }
            normalize={normalizePositiveIntegerValuesInput}
            rules={[
              {
                required: true,
                message: t(
                  'storageSystem.form.financialMetrics.investmentLag.requiredMessage'
                ),
              },
              {
                validator(_, value) {
                  const minValue = parseFloat(value);
                  if (isNaN(minValue) || minValue < 0) {
                    return Promise.reject(
                      new Error(
                        t(
                          'storageSystem.form.financialMetrics.validation.valueBetween0And100'
                        )
                      )
                    );
                  } else if (minValue > 100) {
                    form?.setFieldsValue({ investmentLag: 100 });
                    return Promise.resolve();
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              type="number"
              disabled={disabled}
              placeholder={t(
                'storageSystem.form.financialMetrics.investmentLag.placeholder'
              )}
              addonAfter={t('storageSystem.form.common.year')}
            />
          </Form.Item>
          {yearCount > 0 && (
            <Col className="mb-24">
              <Divider
                dashed
                style={{
                  marginBottom: '-12px',
                  borderColor: theme.colors.grayLightAccent,
                }}
                orientation="left"
              >
                <Typography.Text>
                  {t(
                    'storageSystem.form.financialMetrics.capexDistribution.title'
                  )}
                </Typography.Text>
              </Divider>
              <Row className="preop-capex">
                <Form.List
                  name="preopCapexDistribution"
                  initialValue={Array(yearCount).fill({})}
                >
                  {(subFields) => {
                    const isOperationInitiation = 0;
                    const reversedKeys = subFields
                      .map((item) => item.key)
                      .reverse();

                    const updatedSubFields = subFields.map((item, index) => ({
                      ...item,
                      key: reversedKeys[index],
                    }));
                    return (
                      <Row gutter={[12, 12]}>
                        {updatedSubFields.map((subField) => (
                          <Col span={12} key={subField.key}>
                            <Form.Item
                              key={`preOperationYear-${subField.key}`}
                              name={subField.name}
                              label={
                                <Flex gap={8} align="center">
                                  <Typography.Text className="fs-14-regular text-black-color">
                                    {subField.key === isOperationInitiation
                                      ? t(
                                          'storageSystem.form.financialMetrics.capexDistribution.operationInitiationYear.label'
                                        )
                                      : t(
                                          'storageSystem.form.financialMetrics.capexDistribution.preOperationYear.label',
                                          { year: subField.key }
                                        )}
                                  </Typography.Text>
                                  <RTooltip
                                    title={
                                      subField.key === isOperationInitiation
                                        ? t(
                                            'storageSystem.form.financialMetrics.capexDistribution.operationInitiationYear.tooltip.title'
                                          )
                                        : t(
                                            'storageSystem.form.financialMetrics.capexDistribution.preOperationYear.tooltip.title',
                                            { year: subField.key }
                                          )
                                    }
                                    description={
                                      subField.key === isOperationInitiation
                                        ? t(
                                            'storageSystem.form.financialMetrics.capexDistribution.operationInitiationYear.tooltip.description'
                                          )
                                        : t(
                                            'storageSystem.form.financialMetrics.capexDistribution.preOperationYear.tooltip.description',
                                            { year: subField.key }
                                          )
                                    }
                                  >
                                    <IconInfo />
                                  </RTooltip>
                                </Flex>
                              }
                              rules={[
                                {
                                  required: true,
                                  message: t(
                                    'storageSystem.form.financialMetrics.capexDistribution.preOperationYear.requiredMessage',
                                    { year: subField.key }
                                  ),
                                },
                                {
                                  validator(_, value) {
                                    const minValue = parseFloat(value);
                                    if (isNaN(minValue) || minValue < 0) {
                                      return Promise.reject(
                                        new Error(
                                          t(
                                            'storageSystem.form.financialMetrics.validation.valueBetween0And100'
                                          )
                                        )
                                      );
                                    } else if (minValue > 100) {
                                      return Promise.reject(
                                        new Error(
                                          t(
                                            'storageSystem.form.financialMetrics.validation.valueBetween0And100'
                                          )
                                        )
                                      );
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]}
                            >
                              <Input
                                disabled={disabled}
                                placeholder={t(
                                  'storageSystem.form.financialMetrics.capexDistribution.preOperationYear.inputPlaceholder',
                                  { year: subField.key }
                                )}
                                addonAfter="%"
                              />
                            </Form.Item>
                          </Col>
                        ))}
                      </Row>
                    );
                  }}
                </Form.List>
              </Row>
            </Col>
          )}
        </Col>
        <Col span={12} className="pl-8">
          <Form.Item<StorageSystemForm>
            wrapperCol={{ span: 24 }}
            name="bosLifespan"
            label={t('storageSystem.form.financialMetrics.lifespan.label')}
            normalize={normalizePositiveNumericValuesInput}
            tooltip={{
              title: (
                <div>
                  <>
                    <Typography.Text className="fs-14-semi-bold">
                      {t(
                        'storageSystem.form.financialMetrics.lifespan.tooltip.title'
                      )}
                    </Typography.Text>
                    <div className="horizontal-line mt-4"></div>
                  </>
                  <Typography.Text className="fs-14-regular smoke mt-4">
                    {t(
                      'storageSystem.form.financialMetrics.lifespan.tooltip.description'
                    )}
                  </Typography.Text>
                </div>
              ),
              styles: {
                body: {
                  padding: theme.spacing.spacing12 + 'px',
                  color: 'inherit',
                },
              },
              color: theme.colors.light,
            }}
            rules={[
              {
                required: true,
                message: t(
                  'storageSystem.form.financialMetrics.lifespan.requiredMessage'
                ),
              },
            ]}
          >
            <Input
              addonAfter={t('storageSystem.form.common.year')}
              disabled={disabled}
              placeholder={t(
                'storageSystem.form.financialMetrics.lifespan.placeholder'
              )}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Form.Item<StorageSystemForm>
            name="loanFinancing"
            label={t('storageSystem.form.financialMetrics.loanFinancing.label')}
            rules={[
              {
                required: true,
                message: t(
                  'storageSystem.form.financialMetrics.loanFinancing.requiredMessage'
                ),
              },
            ]}
          >
            <Radio.Group
              onChange={handleLoanFinancingChange}
              name="loanFinancing"
              value={loanFinancing}
              buttonStyle="solid"
              disabled={disabled}
            >
              <Radio.Button value={0}>
                {t(
                  'storageSystem.form.financialMetrics.loanFinancing.fullEquity'
                )}
              </Radio.Button>
              <Radio.Button value={1}>
                {t(
                  'storageSystem.form.financialMetrics.loanFinancing.loanFinancing'
                )}
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Row gutter={16}>
            {loanFinancing === 1 && (
              <Col span={12} className="mb-24">
                <Divider
                  dashed
                  style={{
                    marginBottom: '-12px',
                    marginTop: '-12px',
                    marginLeft: '-6px',
                    borderColor: theme.colors.grayLightAccent,
                  }}
                  orientation="left"
                >
                  <Typography.Text className="fs-14-bold">
                    {t(
                      'storageSystem.form.financialMetrics.loanFinancingEnergyStorageComponents.label'
                    )}
                  </Typography.Text>
                </Divider>
                <Row gutter={12} className="w-100 preop-capex">
                  <Col span={24}>
                    <Form.Item<StorageSystemForm>
                      name="loanFundedCapexRatioBattery"
                      label={t(
                        'storageSystem.form.financialMetrics.loanFundedCapexRatio.label'
                      )}
                      normalize={normalizePositiveNumericValuesInput}
                      rules={[
                        {
                          required: true,
                          message: t(
                            'storageSystem.form.financialMetrics.loanFundedCapexRatio.requiredMessage'
                          ),
                        },
                        {
                          validator(_, value) {
                            const minValue = parseFloat(value);
                            if (isNaN(minValue) || minValue < 0) {
                              return Promise.reject(
                                new Error(
                                  t(
                                    'storageSystem.form.financialMetrics.validation.valueBetween0And100'
                                  )
                                )
                              );
                            } else if (minValue > 100) {
                              form?.setFieldsValue({
                                loanFundedCapexRatioBattery: 100,
                              });
                              return Promise.resolve();
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        placeholder={t(
                          'storageSystem.form.financialMetrics.loanFundedCapexRatio.placeholder'
                        )}
                        addonAfter="%"
                        disabled={disabled}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item<StorageSystemForm>
                      name="loanRepaymentPeriodBattery"
                      label={t(
                        'storageSystem.form.financialMetrics.loanRepaymentPeriod.label'
                      )}
                      normalize={normalizePositiveIntegerValuesInput}
                      rules={[
                        {
                          required: true,
                          message: t(
                            'storageSystem.form.financialMetrics.loanRepaymentPeriod.requiredMessage'
                          ),
                        },
                        {
                          validator(_, value) {
                            const minValue = parseFloat(value);
                            if (isNaN(minValue) || minValue < 1) {
                              return Promise.reject(
                                new Error(
                                  t(
                                    'storageSystem.form.financialMetrics.validation.valueBetween0And100'
                                  )
                                )
                              );
                            } else if (minValue > 100) {
                              form?.setFieldsValue({
                                loanRepaymentPeriodBattery: 100,
                              });
                              return Promise.resolve();
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        placeholder={t(
                          'storageSystem.form.financialMetrics.loanRepaymentPeriod.placeholder'
                        )}
                        onChange={handleLoanRepaymentPeriodChangeBattery}
                        addonAfter={t('storageSystem.form.common.year')}
                        disabled={disabled}
                      />
                    </Form.Item>
                  </Col>
                  {loanRepaymentPeriodBattery !== undefined &&
                    loanRepaymentPeriodBattery !== null &&
                    loanRepaymentPeriodBattery !== 0 && (
                      <Col span={24} className="mb-24">
                        <Divider
                          dashed
                          style={{
                            marginBottom: '-12px',
                            marginTop: '-12px',
                            marginLeft: '-6px',
                            borderColor: theme.colors.grayLightAccent,
                          }}
                          orientation="left"
                        >
                          <Typography.Text>
                            {t(
                              'storageSystem.form.financialMetrics.annualInterestExpense.title'
                            )}
                          </Typography.Text>
                        </Divider>
                        <Row gutter={12} className="w-100 preop-capex">
                          {Array.from(
                            { length: loanRepaymentPeriodBattery },
                            (_, index) => (
                              <Col span={12} key={index}>
                                <Form.Item<StorageSystemForm>
                                  name={['yearlyInterestExpenseBattery', index]}
                                  label={t(
                                    'storageSystem.form.financialMetrics.annualInterestExpense.postOperationYear.label',
                                    { year: index + 1 }
                                  )}
                                  normalize={
                                    normalizePositiveNumericValuesInput
                                  }
                                  rules={[
                                    {
                                      required: true,
                                      message: t(
                                        'storageSystem.form.financialMetrics.annualInterestExpense.postOperationYear.requiredMessage',
                                        { year: index + 1 }
                                      ),
                                    },
                                  ]}
                                >
                                  <Input
                                    placeholder={t(
                                      'storageSystem.form.financialMetrics.annualInterestExpense.postOperationYear.placeholder',
                                      { year: index + 1 }
                                    )}
                                    addonAfter={currencySymbol}
                                    disabled={disabled}
                                  />
                                </Form.Item>
                              </Col>
                            )
                          )}
                        </Row>
                      </Col>
                    )}
                </Row>
              </Col>
            )}
            {loanFinancing === 1 && (
              <Col span={12} className="mb-24">
                <Divider
                  dashed
                  style={{
                    marginBottom: '-12px',
                    marginTop: '-12px',
                    marginLeft: '-6px',
                    borderColor: theme.colors.grayLightAccent,
                  }}
                  orientation="left"
                >
                  <Typography.Text className="fs-14-bold">
                    {t(
                      'storageSystem.form.financialMetrics.loanFinancingBalanceOfSystem.label'
                    )}
                  </Typography.Text>
                </Divider>
                <Row gutter={12} className="w-100 preop-capex">
                  <Col span={24}>
                    <Form.Item<StorageSystemForm>
                      name="loanFundedCapexRatioBos"
                      label={t(
                        'storageSystem.form.financialMetrics.loanFundedCapexRatio.label'
                      )}
                      normalize={normalizePositiveNumericValuesInput}
                      rules={[
                        {
                          required: true,
                          message: t(
                            'storageSystem.form.financialMetrics.loanFundedCapexRatio.requiredMessage'
                          ),
                        },
                        {
                          validator(_, value) {
                            const minValue = parseFloat(value);
                            if (isNaN(minValue) || minValue < 0) {
                              return Promise.reject(
                                new Error(
                                  t(
                                    'storageSystem.form.financialMetrics.validation.valueBetween0And100'
                                  )
                                )
                              );
                            } else if (minValue > 100) {
                              form?.setFieldsValue({
                                loanFundedCapexRatioBos: 100,
                              });
                              return Promise.resolve();
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        placeholder={t(
                          'storageSystem.form.financialMetrics.loanFundedCapexRatio.placeholder'
                        )}
                        addonAfter="%"
                        disabled={disabled}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item<StorageSystemForm>
                      name="loanRepaymentPeriodBos"
                      label={t(
                        'storageSystem.form.financialMetrics.loanRepaymentPeriod.label'
                      )}
                      normalize={normalizePositiveIntegerValuesInput}
                      rules={[
                        {
                          required: true,
                          message: t(
                            'storageSystem.form.financialMetrics.loanRepaymentPeriod.requiredMessage'
                          ),
                        },
                        {
                          validator(_, value) {
                            const minValue = parseFloat(value);
                            if (isNaN(minValue) || minValue < 1) {
                              return Promise.reject(
                                new Error(
                                  t(
                                    'storageSystem.form.financialMetrics.validation.valueBetween1And100'
                                  )
                                )
                              );
                            } else if (minValue > 100) {
                              form?.setFieldsValue({
                                loanRepaymentPeriodBos: 100,
                              });
                              return Promise.resolve();
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        placeholder={t(
                          'storageSystem.form.financialMetrics.loanRepaymentPeriod.placeholder'
                        )}
                        onChange={handleLoanRepaymentPeriodChangeBos}
                        addonAfter={t('storageSystem.form.common.year')}
                        disabled={disabled}
                      />
                    </Form.Item>
                  </Col>
                  {loanRepaymentPeriodBos !== undefined &&
                    loanRepaymentPeriodBos !== null &&
                    loanRepaymentPeriodBos !== 0 && (
                      <Col span={24} className="mb-24">
                        <Divider
                          dashed
                          style={{
                            marginBottom: '-12px',
                            marginTop: '-12px',
                            marginLeft: '-6px',
                            borderColor: theme.colors.grayLightAccent,
                          }}
                          orientation="left"
                        >
                          <Typography.Text>
                            {t(
                              'storageSystem.form.financialMetrics.annualInterestExpense.title'
                            )}
                          </Typography.Text>
                        </Divider>
                        <Row gutter={12} className="w-100 preop-capex">
                          {Array.from(
                            { length: loanRepaymentPeriodBos },
                            (_, index) => (
                              <Col span={12} key={index}>
                                <Form.Item<StorageSystemForm>
                                  name={['yearlyInterestExpenseBos', index]}
                                  label={t(
                                    'storageSystem.form.financialMetrics.annualInterestExpense.postOperationYear.label',
                                    { year: index + 1 }
                                  )}
                                  normalize={
                                    normalizePositiveNumericValuesInput
                                  }
                                  rules={[
                                    {
                                      required: true,
                                      message: t(
                                        'storageSystem.form.financialMetrics.annualInterestExpense.postOperationYear.requiredMessage',
                                        { year: index + 1 }
                                      ),
                                    },
                                  ]}
                                >
                                  <Input
                                    placeholder={t(
                                      'storageSystem.form.financialMetrics.annualInterestExpense.postOperationYear.placeholder',
                                      { year: index + 1 }
                                    )}
                                    addonAfter={currencySymbol}
                                    disabled={disabled}
                                  />
                                </Form.Item>
                              </Col>
                            )
                          )}
                        </Row>
                      </Col>
                    )}
                </Row>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
      <Row className="mb-24">
        <Col span={12} className="pr-8">
          <Form.Item<StorageSystemForm>
            wrapperCol={{ span: 24 }}
            name="depreciationYearsBattery"
            label={t(
              'storageSystem.form.financialMetrics.depreciationYearsEnergyStorageComponents.label'
            )}
            rules={[
              {
                required: true,
                message: t(
                  'storageSystem.form.financialMetrics.depreciationYearsEnergyStorageComponents.requiredMessage'
                ),
              },
            ]}
            normalize={normalizePositiveNumericValuesInput}
          >
            <Input
              addonAfter={t('storageSystem.form.common.year')}
              disabled={disabled}
              placeholder={t(
                'storageSystem.form.financialMetrics.depreciationYearsEnergyStorageComponents.placeholder'
              )}
            />
          </Form.Item>
        </Col>
        <Col span={12} className="pl-8">
          <Form.Item<StorageSystemForm>
            wrapperCol={{ span: 24 }}
            name="depreciationYearsBos"
            label={t(
              'storageSystem.form.financialMetrics.depreciationYearsBalanceOfSystem.label'
            )}
            rules={[
              {
                required: true,
                message: t(
                  'storageSystem.form.financialMetrics.depreciationYearsBalanceOfSystem.requiredMessage'
                ),
              },
            ]}
            normalize={normalizePositiveNumericValuesInput}
          >
            <Input
              addonAfter={t('storageSystem.form.common.year')}
              disabled={disabled}
              placeholder={t(
                'storageSystem.form.financialMetrics.depreciationYearsBalanceOfSystem.placeholder'
              )}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={8} className="pr-8">
          <Form.Item<StorageSystemForm>
            wrapperCol={{ span: 24 }}
            name="batteryCapexValue"
            label={t(
              'storageSystem.form.financialMetrics.energyStorageComponents.label'
            )}
            rules={[
              {
                required: true,
                message: t(
                  'storageSystem.form.financialMetrics.energyStorageComponents.requiredMessage'
                ),
              },
            ]}
            normalize={normalizePositiveNumericValuesInput}
          >
            <Input
              disabled={disabled}
              placeholder={t(
                'storageSystem.form.financialMetrics.energyStorageComponents.placeholder'
              )}
              addonAfter={energyStorageComponentsUnitOptions}
              onChange={handleNumberChange('batteryCapexValue')}
              onBlur={handleNumberBlur('batteryCapexValue')}
            />
          </Form.Item>
        </Col>
        <Col span={8} className="pl-8 pr-8">
          <Form.Item<StorageSystemForm>
            wrapperCol={{ span: 24 }}
            name="pcsCostValue"
            label={t(
              'storageSystem.form.financialMetrics.powerConversionComponents.label'
            )}
            normalize={normalizePositiveNumericValuesInput}
            rules={[
              {
                required: true,
                message: t(
                  'storageSystem.form.financialMetrics.powerConversionComponents.requiredMessage'
                ),
              },
            ]}
          >
            <Input
              addonAfter={pcsCostUnitOptions}
              disabled={disabled}
              placeholder={t(
                'storageSystem.form.financialMetrics.powerConversionComponents.placeholder'
              )}
              onChange={handleNumberChange('pcsCostValue')}
              onBlur={handleNumberBlur('pcsCostValue')}
            />
          </Form.Item>
        </Col>
        <Col span={8} className="pl-8">
          <Form.Item<StorageSystemForm>
            wrapperCol={{ span: 24 }}
            name="bosCostValue"
            label={t(
              'storageSystem.form.financialMetrics.balanceOfSystem.label'
            )}
            rules={[
              {
                required: true,
                message: t(
                  'storageSystem.form.financialMetrics.balanceOfSystem.requiredMessage'
                ),
              },
            ]}
          >
            <Input
              addonAfter={bosCostUnitOptions}
              disabled={disabled}
              placeholder={t(
                'storageSystem.form.financialMetrics.balanceOfSystem.placeholder'
              )}
              onChange={handleNumberChange('bosCostValue')}
              onBlur={handleNumberBlur('bosCostValue')}
            />
          </Form.Item>
        </Col>
        <Col span={8} className="pr-8">
          <Form.Item<StorageSystemForm>
            wrapperCol={{ span: 24 }}
            name="fixedOpexValue"
            label={t(
              'storageSystem.form.financialMetrics.fixedOpexValue.label'
            )}
            rules={[
              {
                required: true,
                message: t(
                  'storageSystem.form.financialMetrics.fixedOpexValue.requiredMessage'
                ),
              },
            ]}
          >
            <Input
              addonAfter={currencySymbol}
              disabled={disabled}
              placeholder={t(
                'storageSystem.form.financialMetrics.fixedOpexValue.placeholder'
              )}
              onChange={handleNumberChange('fixedOpexValue')}
              onBlur={handleNumberBlur('fixedOpexValue')}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form.Item>
  );
};

export default FinancialMetrics;
